import { Component, DestroyRef, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImagingStudy } from 'fhir/r4';
import { RequiredImagingStudyResultsTableComponent, ImagingStudyRow } from '../../shared/required-imaging-study-results-table/required-imaging-study-results-table.component';

export interface SelectedImagingStudiesDialogData {
    selectedImagingStudies: ImagingStudy[];
    editable?: boolean;
    serviceRequestIds?: string[];
    patientId?: string | null;
    onSelectionChanged?: (selectedRows: ImagingStudy[]) => void;
}

@Component({
    selector: 'app-selected-imaging-studies',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatTableModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        RequiredImagingStudyResultsTableComponent
    ],
    templateUrl: './selected-imaging-studies.component.html',
    styleUrl: './selected-imaging-studies.component.scss'
})
export class SelectedImagingStudiesComponent implements OnInit, OnChanges {
    @Input() selectedImagingStudies: ImagingStudy[] = [];
    @Input() editable = true;
    @Input() serviceRequestIds?: string[];
    @Input() patientId?: string | null;

    @Output() selectionChanged = new EventEmitter<ImagingStudy[]>();

    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(MatDialog);
    private readonly data = inject(MAT_DIALOG_DATA, { optional: true }) as SelectedImagingStudiesDialogData | null;
    dialogRef = inject(MatDialogRef<SelectedImagingStudiesComponent>, { optional: true }) as MatDialogRef<SelectedImagingStudiesComponent> | null;

    dataSource = new MatTableDataSource<ImagingStudy>([]);
    selection = new SelectionModel<ImagingStudy>(true, []);

    tableColumns: string[] = ['select', 'description', 'status', 'modality', 'started'];

    constructor() {
        const data = this.data;
        if (!data) return;

        this.selectedImagingStudies = data.selectedImagingStudies ?? [];
        this.editable = data.editable ?? true;
        if (data.serviceRequestIds) this.serviceRequestIds = data.serviceRequestIds ?? [];
        this.patientId = data.patientId ?? null;
    }

    close(): void {
        this.dialogRef?.close({ selectedImagingStudies: this.selectedImagingStudies });
    }

    ngOnInit(): void {
        this.rebuildColumns();
        this.setRows(this.selectedImagingStudies);

        this.selection.changed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const selected = [...this.selection.selected];
                this.selectionChanged.emit(selected);
                this.data?.onSelectionChanged?.(selected);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['editable']) {
            this.rebuildColumns();
        }

        if (changes['selectedImagingStudies']) {
            this.setRows(this.selectedImagingStudies);
        }
    }

    private rebuildColumns(): void {
        this.tableColumns = ['select', 'description', 'status', 'modality', 'started'];
        if (this.editable) this.tableColumns.push('actions');
    }

    private setRows(rows: ImagingStudy[]): void {
        const next = rows ?? [];
        this.dataSource.data = next;

        // Treat selection as "included" rows.
        this.selection.clear();
        if (next.length) this.selection.select(...next);
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numRows > 0 && numSelected === numRows;
    }

    toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.data);
    }

    checkboxLabel(row?: ImagingStudy): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
    }

    deleteRow(row: ImagingStudy): void {
        if (!this.editable) return;

        const next = (this.dataSource.data ?? []).filter(r => r !== row);
        this.selectedImagingStudies = next;
        this.setRows(next);
    }

    deleteSelected(): void {
        if (!this.editable) return;
        const selected = new Set(this.selection.selected);
        if (selected.size === 0) return;

        const next = (this.dataSource.data ?? []).filter(r => !selected.has(r));
        this.selectedImagingStudies = next;
        this.setRows(next);
    }

    imagingTitle(img: any): string {
        return img?.description ?? `ImagingStudy ${(img?.id ?? '').toString()}`;
    }

    imagingModality(img: any): string {
        const mods = (img?.modality ?? []) as any[];
        if (!mods.length) return '—';
        return mods.map((m: any) => m?.display ?? m?.code).filter(Boolean).join(', ') || '—';
    }

    imagingStarted(img: any): string {
        return img?.started ?? '—';
    }

    private imagingKey(img: any): string {
        const id = img?.id;
        if (id) return `id:${id}`;

        const desc = img?.description ?? '';
        const started = img?.started ?? '';
        return `fallback:${desc}|${started}`;
    }

    addMore(): void {
        const dialogRef = this.dialog.open(RequiredImagingStudyResultsTableComponent, {
            width: '900px',
            maxHeight: '93vh',
            data: {
                serviceRequestIds: this.serviceRequestIds ?? [],
                patientId: this.patientId ?? null,
                forSelection: true,
                imagingStudiesToMatch: this.selectedImagingStudies ?? []
            }
        });

        dialogRef.afterClosed().subscribe((result: any) => {
            const pickedRows = (result?.rows ?? []) as ImagingStudyRow[];
            const picked = pickedRows.map((r) => r?.imagingStudy).filter(Boolean) as ImagingStudy[];

            const current = this.dataSource.data ?? [];
            const merged = new Map<string, ImagingStudy>();
            for (const img of current) merged.set(this.imagingKey(img), img);
            for (const img of picked) merged.set(this.imagingKey(img), img);

            const next = Array.from(merged.values());
            this.selectedImagingStudies = next;
            this.setRows(next);
        });
    }
}
